import {
  IS_AGENT_SELECT,
  IS_CHAMPION_SELECT,
  IS_LOLSTATS,
  IS_PROBUILDS,
  IS_TESTING,
  IS_TFTCOMPS,
} from "@/__main__/constants.mjs";
import mainRefs from "@/__main__/refs.mjs";
import { devDebug, devWarn } from "@/util/dev.mjs";
import globals from "@/util/global-whitelist.mjs";

const MAX_QUEUE_LENGTH = 10;
const BLACKLISTED_REGEXP = /\/[^/]+\/(overlay)(\/|$)/;

let resolveConsent;
let rejectConsent;

export const didInitConsent = new Promise((resolve, reject) => {
  if (IS_TESTING) resolve();
  resolveConsent = resolve;
  rejectConsent = reject;
});

let didInit = false;
export async function setupConsent() {
  if (BLACKLISTED_REGEXP.test(globals.location?.pathname)) return;

  if (didInit) return;
  didInit = true;

  await mainRefs.didRender;

  let url =
    "https://cmp.inmobi.com/choice/gZzLr0R4qLK_S/blitz.gg/choice.js?tag_version=V3";
  if (IS_PROBUILDS) {
    url =
      "https://cmp.inmobi.com/choice/gZzLr0R4qLK_S/probuilds.net/choice.js?tag_version=V3";
  }
  if (IS_CHAMPION_SELECT) {
    url =
      "https://cmp.inmobi.com/choice/gZzLr0R4qLK_S/championselect.net/choice.js?tag_version=V3";
  }
  if (IS_AGENT_SELECT) {
    url =
      "https://cmp.inmobi.com/choice/gZzLr0R4qLK_S/agentselect.net/choice.js?tag_version=V3";
  }
  if (IS_LOLSTATS) {
    url =
      "https://cmp.inmobi.com/choice/gZzLr0R4qLK_S/lolstats.com/choice.js?tag_version=V3";
  }
  if (IS_TFTCOMPS) {
    url =
      "https://cmp.inmobi.com/choice/gZzLr0R4qLK_S/tftcomps.gg/choice.js?tag_version=V3";
  }

  const element = globals.document.createElement("script");
  let uspTries = 0;
  const uspTriesLimit = 5;

  element.async = true;
  element.src = url;
  globals.document.body.appendChild(element);

  function makeStub() {
    const TCF_LOCATOR_NAME = "__tcfapiLocator";
    const queue = [];
    let cmpFrame;
    // eslint-disable-next-line no-restricted-properties
    let g = globalThis;

    function addFrame() {
      const otherCMP = g.frames[TCF_LOCATOR_NAME];

      if (!otherCMP) {
        const iframe = globals.document.createElement("iframe");
        iframe.style.cssText = "display:none";
        iframe.name = TCF_LOCATOR_NAME;
        globals.document.body.appendChild(iframe);
      }

      return !otherCMP;
    }

    function tcfAPIHandler(...args) {
      let gdprApplies;

      if (!args.length) {
        return queue;
      }
      if (args[0] === "setGdprApplies") {
        if (args.length > 3 && args[2] === 2 && typeof args[3] === "boolean") {
          gdprApplies = args[3];
          if (typeof args[2] === "function") {
            args[2]("set", true);
          }
        }
      } else if (args[0] === "ping") {
        const retr = {
          gdprApplies: gdprApplies,
          cmpLoaded: false,
          cmpStatus: "stub",
        };
        if (typeof args[2] === "function") {
          args[2](retr);
        }
      } else {
        if (args[0] === "init" && typeof args[3] === "object") {
          args[3] = Object.assign(args[3], { tag_version: "V3" });
        }
        queue.push(args);
        if (queue.length > MAX_QUEUE_LENGTH) {
          delete queue[queue.length - 1 - MAX_QUEUE_LENGTH];
        }
      }
    }

    function postMessageEventHandler(event) {
      const msgIsString = typeof event.data === "string";
      let json = {};

      try {
        if (msgIsString) {
          json = JSON.parse(event.data);
        } else {
          json = event.data;
        }
      } catch (e) {
        devDebug("[consent] MESSAGE HANDLER", { error: e, data: event.data });
      }

      const payload = json?.__tcfapiCall;

      if (!payload) return;

      g?.frames?.__tcfapi(
        payload.command,
        payload.version,
        (retValue, success) => {
          let returnMsg = {
            __tcfapiReturn: {
              returnValue: retValue,
              success: success,
              callId: payload.callId,
            },
          };
          if (msgIsString) {
            returnMsg = JSON.stringify(returnMsg);
          }
          if (event?.source?.postMessage) {
            event.source.postMessage(returnMsg, "*");
          }
        },
        payload.parameter,
      );
    }

    while (g) {
      try {
        if (g?.frames?.[TCF_LOCATOR_NAME]) {
          cmpFrame = g;
          break;
        }
      } catch (_) {
        /* */
      }

      // eslint-disable-next-line no-restricted-properties
      if (g === globalThis.top) {
        break;
      }
      g = g.parent;
    }
    if (!cmpFrame) {
      addFrame();
      g.__tcfapi = tcfAPIHandler;
      g.addEventListener("message", postMessageEventHandler, false);
    }
  }

  makeStub();

  function makeGppStub() {
    const CMP_ID = 10;
    const SUPPORTED_APIS = [
      "2:tcfeuv2",
      "6:uspv1",
      "7:usnatv1",
      "8:usca",
      "9:usvav1",
      "10:uscov1",
      "11:usutv1",
      "12:usctv1",
    ];

    // eslint-disable-next-line no-restricted-properties
    globalThis.__gpp_addFrame = (n) => {
      // eslint-disable-next-line no-restricted-properties
      if (!globalThis.frames[n]) {
        if (globals.document.body) {
          const i = globals.document.createElement("iframe");
          i.style.cssText = "display:none";
          i.name = n;
          globals.document.body.appendChild(i);
        } else {
          // eslint-disable-next-line no-restricted-properties
          globalThis.setTimeout(globalThis.__gpp_addFrame, 10, n);
        }
      }
    };

    /* eslint-disable no-restricted-properties*/
    globalThis.__gpp_stub = (...args) => {
      globalThis.__gpp.queue = globalThis.__gpp.queue || [];
      globalThis.__gpp.events = globalThis.__gpp.events || [];
      // eslint-enable

      if (!args.length || (args.length === 1 && args[0] === "queue")) {
        return globalThis.__gpp.queue;
      }

      if (args.length === 1 && args[0] === "events") {
        return globalThis.__gpp.events;
      }

      const cmd = args[0];
      const clb = args.length > 1 ? args[1] : null;
      const par = args.length > 2 ? args[2] : null;
      if (cmd === "ping") {
        clb(
          {
            gppVersion: "1.1",
            cmpStatus: "stub",
            cmpDisplayStatus: "hidden",
            signalStatus: "not ready",
            supportedAPIs: SUPPORTED_APIS,
            cmpId: CMP_ID,
            sectionList: [],
            applicableSections: [-1],
            gppString: "",
            parsedSections: {},
          },
          true,
        );
      } else if (cmd === "addEventListener") {
        globalThis.__gpp.lastId = (globalThis.__gpp.lastId || 0) + 1;
        const lnr = globalThis.__gpp.lastId;
        const q = globalThis.__gpp.events;
        q.push({
          id: lnr,
          callback: clb,
          parameter: par,
        });
        if (q.length > MAX_QUEUE_LENGTH) {
          delete q[q.length - 1 - MAX_QUEUE_LENGTH];
        }
        clb(
          {
            eventName: "listenerRegistered",
            listenerId: lnr,
            data: true,
            pingData: {
              gppVersion: "1.1",
              cmpStatus: "stub",
              cmpDisplayStatus: "hidden",
              signalStatus: "not ready",
              supportedAPIs: SUPPORTED_APIS,
              cmpId: CMP_ID,
              sectionList: [],
              applicableSections: [-1],
              gppString: "",
              parsedSections: {},
            },
          },
          true,
        );
      } else if (cmd === "removeEventListener") {
        let success = false;
        for (let i = 0; i < globalThis.__gpp.events.length; i++) {
          if (globalThis.__gpp.events?.[i]?.id === par) {
            globalThis.__gpp.events.splice(i, 1);
            success = true;
            break;
          }
        }
        clb(
          {
            eventName: "listenerRemoved",
            listenerId: par,
            data: success,
            pingData: {
              gppVersion: "1.1",
              cmpStatus: "stub",
              cmpDisplayStatus: "hidden",
              signalStatus: "not ready",
              supportedAPIs: SUPPORTED_APIS,
              cmpId: CMP_ID,
              sectionList: [],
              applicableSections: [-1],
              gppString: "",
              parsedSections: {},
            },
          },
          true,
        );
      } else if (cmd === "hasSection") {
        clb(false, true);
      } else if (cmd === "getSection" || cmd === "getField") {
        clb(null, true);
      } else {
        const q = globalThis.__gpp.queue;
        q.push([].slice.apply(args));
        if (q.length > MAX_QUEUE_LENGTH) {
          delete q[q.length - 1 - MAX_QUEUE_LENGTH];
        }
      }
    };

    globalThis.__gpp_msghandler = (event) => {
      const msgIsString = typeof event.data === "string";
      let json = null;

      try {
        json = msgIsString ? JSON.parse(event.data) : event.data;
      } catch (_) {
        /* */
      }

      if (typeof json === "object" && json !== null && "__gppCall" in json) {
        const i = json.__gppCall;
        globalThis.__gpp(
          i.command,
          (retValue, success) => {
            const returnMsg = {
              __gppReturn: {
                returnValue: retValue,
                success: success,
                callId: i.callId,
              },
            };
            event.source.postMessage(
              msgIsString ? JSON.stringify(returnMsg) : returnMsg,
              "*",
            );
          },
          "parameter" in i ? i.parameter : null,
          "version" in i ? i.version : "1.1",
        );
      }
    };

    if (!("__gpp" in globalThis) || typeof globalThis.__gpp !== "function") {
      globalThis.__gpp = globalThis.__gpp_stub;
      globalThis.addEventListener(
        "message",
        globalThis.__gpp_msghandler,
        false,
      );
      globalThis.__gpp_addFrame("__gppLocator");
    }
  }

  makeGppStub();

  const uspStubFunction = (...args) => {
    if (typeof globalThis.__uspapi !== typeof uspStubFunction) {
      setTimeout(() => {
        if (typeof globalThis.__uspapi !== "undefined") {
          globalThis.__uspapi.apply(globalThis.__uspapi, args);
        }
      }, 500);
    }
  };

  let uspInterval;

  const checkIfUspIsReady = () => {
    uspTries++;
    if (globalThis.__uspapi === uspStubFunction && uspTries < uspTriesLimit) {
      devWarn("USP is not accessible");
    } else {
      clearInterval(uspInterval);
    }
  };

  if (typeof globalThis.__uspapi === "undefined") {
    globalThis.__uspapi = uspStubFunction;
    // eslint-disable-next-line no-restricted-globals
    uspInterval = setInterval(checkIfUspIsReady, 6000);
  }

  // eslint-disable-next-line no-restricted-properties
  globalThis.__tcfapi("addEventListener", 2, (tcData, success) => {
    if (!success) {
      devWarn("[consent] TCF failure!", tcData);
      rejectConsent();
      return;
    }
    resolveConsent();
    devDebug("[consent] TCF success", { success, tcData });

    if (!tcData?.gdprApplies) return;

    if (tcData?.eventStatus === "tcloaded") {
      // we have consent, get the tcData string and do the ad request
      devDebug("[consent] TC loaded");
    } else if (tcData?.eventStatus === "useractioncomplete") {
      // user just gave consent, get the tcdata string and do the
      // ad request
      devDebug("[consent] TC useractioncomplete");
    } else {
      devDebug("[consent] UNKNOWN TC DATA", tcData);
    }
  });
}

export function teardownConsent() {}
